import React, { useState, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { PlusIcon, MinusIcon } from 'lucide-react';
interface FAQItemProps {
  question: string;
  answer: string;
  index: number;
  isOpen: boolean;
  onToggle: () => void;
}
function FAQItem({
  question,
  answer,
  index,
  isOpen,
  onToggle
}: FAQItemProps) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-50px'
  });
  return <motion.div ref={ref} className="border-b border-rose-100 last:border-b-0" initial={{
    opacity: 0,
    y: 20
  }} animate={isInView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.4,
    delay: index * 0.08
  }} data-id="element-39">
      <button onClick={onToggle} className="w-full py-6 flex items-center justify-between text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-500 focus-visible:ring-offset-2 rounded-lg" aria-expanded={isOpen} data-id="element-40">
        <span className="text-lg font-semibold text-slate-900 pr-4" data-id="element-41">
          {question}
        </span>
        <span className="flex-shrink-0 w-8 h-8 bg-rose-100 rounded-full flex items-center justify-center text-rose-500" data-id="element-42">
          {isOpen ? <MinusIcon className="w-5 h-5" data-id="element-43" /> : <PlusIcon className="w-5 h-5" data-id="element-44" />}
        </span>
      </button>

      <AnimatePresence initial={false} data-id="element-45">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} className="overflow-hidden" data-id="element-46">
            <p className="pb-6 text-slate-600 leading-relaxed pr-12" data-id="element-47">
              {answer}
            </p>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}
export function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const faqs = [{
    question: '🎨 ¿Necesito saber diseño gráfico para usar este kit?',
    answer: '¡Para nada! Todos los diseños son editables en Canva, que es gratuito y muy fácil de usar. Solo entrás, cambiás el texto y los colores, y listo. Además incluye un manual completo que te guía paso a paso desde cero.'
  }, {
    question: '📥 ¿Cómo recibo los archivos después de comprar?',
    answer: 'La entrega es 100% digital e instantánea. Apenas confirmás el pago, recibís un enlace de descarga directamente en tu email. No hay envíos físicos ni esperas. Podés acceder a todo desde tu celular o computadora.'
  }, {
    question: '💰 ¿Puedo vender los diseños que cree con este kit?',
    answer: '¡Sí! El kit incluye licencia comercial. Podés editar los diseños, personalizarlos con tu marca y venderlos en Etsy, Instagram, WhatsApp, Shopify o donde quieras. Te quedás con el 100% de tus ganancias, sin límite de ventas.'
  }, {
    question: '🖨️ ¿Necesito una impresora especial?',
    answer: 'No necesariamente. Podés imprimir en cualquier impresora hogareña o llevar los archivos a una imprenta. El manual incluye recomendaciones de papeles, gramajes y configuraciones para que el resultado sea profesional sin importar la impresora que uses.'
  }, {
    question: '📂 ¿Cómo están organizados los más de 25.000 diseños?',
    answer: 'Todo está organizado en 26 carpetas temáticas con más de 1.000 diseños cada una: tarjetas de cumpleaños, invitaciones, packaging, banderines, números 3D, flores de papel, etiquetas y mucho más. Además incluye carpetas de bonos especiales como Día de la Madre, etiquetas de colegio y letras 3D.'
  }, {
    question: '🔄 ¿Recibo actualizaciones con nuevos diseños?',
    answer: 'Sí. Una vez que comprás el kit, recibís todas las actualizaciones y nuevos diseños que se vayan agregando, sin costo adicional. Tu kit crece con vos.'
  }, {
    question: '🆘 ¿Qué pasa si tengo algún problema con la descarga?',
    answer: 'Tenés soporte directo. Si tenés algún problema con tu descarga o acceso a los archivos, nos contactás y te ayudamos de inmediato. Tu compra está protegida y tu satisfacción es nuestra prioridad. 🌸'
  }];
  const headerRef = useRef(null);
  const isHeaderInView = useInView(headerRef, {
    once: true
  });
  return <section className="w-full bg-pink-50 py-20 sm:py-28" data-id="element-48">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-49">
        {/* Header */}
        <motion.div ref={headerRef} className="text-center mb-12" initial={{
        opacity: 0,
        y: 30
      }} animate={isHeaderInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} data-id="element-50">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-slate-900 mb-4" data-id="element-51">
            ❓ Preguntas Frecuentes
          </h2>
          <p className="text-xl sm:text-2xl font-bold text-rose-500 mb-4" data-id="element-52">
            Todo lo que Querés Saber Antes de Comprar 🌸
          </p>
          <p className="text-slate-600" data-id="element-53">
            Respondemos las dudas más comunes para que compres con total
            confianza.
          </p>
        </motion.div>

        {/* FAQ Items */}
        <div className="bg-white rounded-3xl shadow-lg p-6 sm:p-8" data-id="element-54">
          {faqs.map((faq, index) => <FAQItem key={index} question={faq.question} answer={faq.answer} index={index} isOpen={openIndex === index} onToggle={() => setOpenIndex(openIndex === index ? null : index)} data-id="element-55" />)}
        </div>
      </div>
    </section>;
}